/**
 * "Confirm it is you": the password again, or Google for an account that only signs in with
 * Google. Asked before switching remote control on — a line to the person's machine that a token
 * lifted from a browser should not be able to open. The session carries on with the fresh token.
 */

import { useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { saveSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { GoogleSignIn } from './GoogleSignIn'

export function ConfirmIdentity({ onConfirmed, onCancel, why }: { onConfirmed: () => void; onCancel?: () => void; why?: string }) {
  const { t } = useI18n()
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const { data: config } = useQuery({ queryKey: ['auth', 'config'], queryFn: api.auth.config, staleTime: 60 * 60_000 })
  const hasPassword = me?.hasPassword !== false

  const confirm = async (body: { password?: string; googleIdToken?: string }): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      saveSession(await api.auth.reauthenticate(body))
      onConfirmed()
    } catch (failure) {
      setError(failure instanceof ApiError && failure.code === 'invalid_credentials' ? t('auth.reauth.wrong') : t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    if (password && !busy) void confirm({ password })
  }

  return (
    <div className="confirm-identity" role="group" aria-label={t('auth.reauth.title')}>
      <div className="row row--tight">
        <ShieldCheck size={15} aria-hidden />
        <span className="small bold">{t('auth.reauth.title')}</span>
      </div>
      <span className="tiny muted">{why ?? t('auth.reauth.why')}</span>

      {hasPassword ? (
        <form className="row row--tight row--wrap" onSubmit={submit}>
          <input
            className="input"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={t('auth.reauth.password')}
            aria-label={t('auth.reauth.password')}
            autoComplete="current-password"
            autoFocus
            disabled={busy}
          />
          <button type="submit" className="btn btn--sm btn--primary" disabled={!password || busy}>
            {t('auth.reauth.confirm')}
          </button>
          {onCancel && (
            <button type="button" className="btn btn--sm btn--ghost" onClick={onCancel}>
              {t('common.cancel')}
            </button>
          )}
        </form>
      ) : (
        <div className="row row--tight row--wrap">
          <GoogleSignIn clientId={config?.googleClientId ?? null} onCredential={(googleIdToken) => void confirm({ googleIdToken })} disabled={busy} />
          {onCancel && (
            <button type="button" className="btn btn--sm btn--ghost" onClick={onCancel}>
              {t('common.cancel')}
            </button>
          )}
        </div>
      )}

      {error && <span className="field__error">{error}</span>}
    </div>
  )
}
